import { useCallback, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ArrowLeft, ArrowRight, Ban, Building2, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorState } from "@/components/ui/states";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { formatApiError, isApiError } from "@/lib/api-error";
import { usePageNavigation } from "@/hooks/use-page-navigation";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { useFilaDeSalvamento } from "@/hooks/use-fila-de-salvamento";
import {
  getPropostaStatusInfo,
  motivoDoBloqueioDaEdicao,
  propostaEhEditavel,
  propostaFoiAceita,
  propostaPodeSerCancelada,
} from "@/utils/proposta-status";
import { passosDaProposta } from "@/utils/proposta-ciclo";
import { formatDateOnly } from "@/utils/dateUtils";
import { financeApi } from "@/services/finance-api";
import {
  useAdicionarItens,
  useAdicionarPeriodoNovo,
  useAtualizarAgenciaDaProposta,
  useAtualizarFreelancerDaProposta,
  useAtualizarProposta,
  useCancelarProposta,
  useGerarDocumento,
  useGerarInvite,
  useMudarCicloDaProposta,
  useMudarCicloDoInvite,
  usePropostaDetail,
  usePropostaOrdensServico,
  useRemoverItem,
} from "@/hooks/use-finance-propostas";
import { ProjetosElegiveisPicker } from "@/components/finance/ProjetosElegiveisPicker";
import { EtapaDados } from "@/components/finance/proposta/EtapaDados";
import { EtapaDocumento } from "@/components/finance/proposta/EtapaDocumento";
import { EtapaOrdensServico } from "@/components/finance/proposta/EtapaOrdensServico";
import { NewPeriodDialog } from "@/components/finance/proposta/NewPeriodDialog";
import { ResumoProposta } from "@/components/finance/proposta/ResumoProposta";
import {
  AcoesDoCicloDaProposta,
  MarcarPassoDialog,
  type PedidoDePasso,
} from "@/components/finance/proposta/CicloDaProposta";
import type {
  AcaoDoCiclo,
  CicloBody,
  FormatoDocumento,
  NovoPeriodoBody,
  PatchAgenciaDaPropostaBody,
  PatchPropostaBody,
  PropostaGrupo,
  PropostaItem,
  PropostaPendencia,
  DocumentoEditavel,
} from "@/types/finance";

const ETAPAS = [
  { id: "dados", numero: "01", rotulo: "Dados da proposta" },
  { id: "documento", numero: "02", rotulo: "Documento" },
  { id: "os", numero: "03", rotulo: "Ordens de serviço" },
] as const;
type Etapa = (typeof ETAPAS)[number]["id"];

const ORIGEM_LABEL: Record<string, string> = {
  vaga: "De vaga aprovada",
  renovacao: "De renovação",
  avulsa: "Avulsa",
};

const MENSAGEM_DO_PASSO: Record<AcaoDoCiclo, string> = {
  enviado: "Envio registrado",
  aceito: "Aceite registrado",
  recusado: "Recusa registrada",
  reaberto: "Reaberto",
};

const CAMPOS_DA_AGENCIA = new Set(["sigla", "razao_social", "cnpj", "endereco"]);

function etapasVisiveis(podeVerOs: boolean) {
  return podeVerOs ? ETAPAS : ETAPAS.filter((etapa) => etapa.id !== "os");
}

function etapaValida(valor: string | null, podeVerOs: boolean): Etapa {
  return etapasVisiveis(podeVerOs).some((etapa) => etapa.id === valor) ? (valor as Etapa) : "dados";
}

function abrirUrlEmNovaAba(obterUrl: () => Promise<string>, aoFalhar: (erro: unknown) => void) {
  const janela = window.open("", "_blank");
  obterUrl()
    .then((url) => {
      if (janela) {
        janela.opener = null;
        janela.location.href = url;
      } else {
        window.location.assign(url);
      }
    })
    .catch((erro) => {
      janela?.close();
      aoFalhar(erro);
    });
}

function imprimirHtml(html: string) {
  const quadro = document.createElement("iframe");
  quadro.setAttribute("sandbox", "allow-same-origin allow-modals");
  quadro.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  quadro.srcdoc = html;
  quadro.onload = () => {
    quadro.contentWindow?.focus();
    quadro.contentWindow?.print();
    setTimeout(() => quadro.remove(), 60_000);
  };
  document.body.appendChild(quadro);
}

export default function PropostaDetalhe() {
  const { id } = useParams<{ id: string }>();
  const propostaId = id ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const { screens } = useUserPermissions();
  const podeVerOs = screens.includes("financeiro_os");
  const etapas = etapasVisiveis(podeVerOs);
  const etapa = etapaValida(searchParams.get("etapa"), podeVerOs);
  const { toast } = useToast();
  const { navigateBack } = usePageNavigation({ pageStorageKey: "propostas_page" });
  const fila = useFilaDeSalvamento();
  const { enfileirar } = fila;

  const propostaQuery = usePropostaDetail(id);
  const proposta = propostaQuery.data;
  const ordensQuery = usePropostaOrdensServico(id, etapa === "os");

  const atualizar = useAtualizarProposta(propostaId);
  const atualizarAgencia = useAtualizarAgenciaDaProposta(propostaId);
  const { mutateAsync: atualizarFreelancerAsync } = useAtualizarFreelancerDaProposta(propostaId);
  const adicionarItens = useAdicionarItens(propostaId);
  const addNewPeriod = useAdicionarPeriodoNovo(propostaId);
  const removerItem = useRemoverItem(propostaId);
  const gerarDocumento = useGerarDocumento(propostaId);
  const gerarInviteDoProfissional = useGerarInvite(propostaId);
  const cancelar = useCancelarProposta(propostaId);
  const mudarCicloDaProposta = useMudarCicloDaProposta(propostaId);
  const mudarCicloDoInvite = useMudarCicloDoInvite();
  const [pedidoDePasso, setPedidoDePasso] = useState<PedidoDePasso | null>(null);

  const [pickerAberto, setPickerAberto] = useState(false);
  const [newPeriodOpen, setNewPeriodOpen] = useState(false);
  const [selecionadosParaAdicionar, setSelecionadosParaAdicionar] = useState<string[]>([]);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [itemParaRemover, setItemParaRemover] = useState<{ item: PropostaItem; grupo: PropostaGrupo } | null>(null);
  const [itemDestacado, setItemDestacado] = useState<string | null>(null);

  const reportarErro = useCallback(
    (erro: unknown) => {
      const formatado = formatApiError(erro);
      toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
    },
    [toast],
  );

  const { mutateAsync: atualizarPropostaAsync } = atualizar;
  const { mutateAsync: atualizarAgenciaAsync } = atualizarAgencia;

  const salvarProposta = useCallback(
    (patch: PatchPropostaBody) => {
      enfileirar(() => atualizarPropostaAsync(patch)).catch(reportarErro);
    },
    [enfileirar, atualizarPropostaAsync, reportarErro],
  );

  const salvarAgencia = useCallback(
    (patch: PatchAgenciaDaPropostaBody) => {
      enfileirar(() => atualizarAgenciaAsync(patch)).catch(reportarErro);
    },
    [enfileirar, atualizarAgenciaAsync, reportarErro],
  );

  const salvarCadastroDaAgencia = useCallback(
    async (patch: PatchAgenciaDaPropostaBody) => {
      await enfileirar(() => atualizarAgenciaAsync(patch));
    },
    [enfileirar, atualizarAgenciaAsync],
  );

  const salvarCnpjDoFreelancer = useCallback(
    (freelancerId: string, cnpj: string | null) => {
      enfileirar(() => atualizarFreelancerAsync({ freelancerId, body: { cnpj } })).catch(reportarErro);
    },
    [enfileirar, atualizarFreelancerAsync, reportarErro],
  );

  const imprimirDocumento = (documento: DocumentoEditavel) => {
    const pedido =
      documento.tipo === "invite"
        ? financeApi.getInvitePreview(propostaId, documento.freelancerId)
        : financeApi.getPropostaPreview(propostaId);
    pedido.then(({ html }) => imprimirHtml(html)).catch(reportarErro);
  };

  const irParaEtapa = (proxima: Etapa) => {
    setSearchParams(
      (atual) => {
        const parametros = new URLSearchParams(atual);
        parametros.set("etapa", proxima);
        return parametros;
      },
      { replace: true },
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const destacarItem = (itemId: string) => {
    setItemDestacado(itemId);
    requestAnimationFrame(() =>
      document.getElementById(`item-${itemId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
    setTimeout(() => setItemDestacado((atual) => (atual === itemId ? null : atual)), 2500);
  };

  const resolverPendencia = (pendencia: PropostaPendencia) => {
    if (pendencia.codigo.includes("omie")) {
      window.open("/financeiro?aba=cadastros-omie", "_blank", "noopener");
      return;
    }
    if (pendencia.codigo === "documento_com_colchetes") {
      irParaEtapa("documento");
      return;
    }
    if (etapa !== "dados") irParaEtapa("dados");
    requestAnimationFrame(() => {
      if (pendencia.item_id) {
        destacarItem(pendencia.item_id);
        return;
      }
      const campo = pendencia.campo ?? (pendencia.codigo === "sem_competencia" ? "competencia" : null);
      const elemento = campo ? document.getElementById(`campo-${campo}`) : null;
      elemento?.scrollIntoView({ behavior: "smooth", block: "center" });
      elemento?.focus();
    });
  };

  const gerar = async () => {
    await fila.aguardar();
    try {
      const resultado = await gerarDocumento.mutateAsync();
      toast({
        title: `Proposta v${resultado.versao} e ${resultado.invites.length} ${resultado.invites.length === 1 ? "invite gerado" : "invites gerados"}`,
        description: resultado.pdf_disponivel
          ? `Código ${resultado.codigo}. Os PDFs estão na etapa Documento.`
          : "O PDF não pôde ser montado agora; as versões ficaram em HTML. Tente gerar de novo em instantes.",
      });
    } catch (erro) {
      if (isApiError(erro) && erro.status === 422) {
        propostaQuery.refetch();
        toast({
          variant: "destructive",
          title: "Faltam dados para gerar o PDF",
          description: "Os itens estão destacados no resumo ao lado.",
        });
        return;
      }
      reportarErro(erro);
    }
  };

  const gerarInvite = async (freelancerId: string, nome: string) => {
    await fila.aguardar();
    const estavaEnviado = proposta?.invites.some(
      (invite) => invite.freelancer_id === freelancerId && invite.status === "enviado",
    );
    try {
      const resultado = await gerarInviteDoProfissional.mutateAsync(freelancerId);
      toast({
        title: `Invite de ${nome} v${resultado.versao} gerado`,
        description: !resultado.pdf_disponivel
          ? "O PDF não pôde ser montado agora; a versão ficou em HTML. Tente gerar de novo em instantes."
          : estavaEnviado
            ? "O invite voltou para Gerado: marque como enviado quando mandar esta versão."
            : "A proposta e os outros invites ficaram como estavam.",
      });
    } catch (erro) {
      if (isApiError(erro) && erro.status === 422) {
        propostaQuery.refetch();
        toast({
          variant: "destructive",
          title: "Faltam dados para gerar o invite",
          description: "Os itens estão destacados no resumo ao lado.",
        });
        return;
      }
      reportarErro(erro);
    }
  };

  const baixarInvite = (inviteId: string, versao: number, formato: FormatoDocumento) => {
    abrirUrlEmNovaAba(
      () => financeApi.getInviteDocumentoDownload(inviteId, versao, formato).then(({ url }) => url),
      reportarErro,
    );
  };

  const baixar = (versao: number, formato: FormatoDocumento) => {
    abrirUrlEmNovaAba(
      () => financeApi.getDocumentoDownload(propostaId, versao, formato).then(({ url }) => url),
      reportarErro,
    );
  };

  const confirmarRemocao = async () => {
    if (!itemParaRemover) return;
    await fila.aguardar();
    try {
      await removerItem.mutateAsync(itemParaRemover.item.id);
      toast({ title: "Período tirado da proposta" });
    } catch (erro) {
      reportarErro(erro);
    } finally {
      setItemParaRemover(null);
    }
  };

  const confirmarAdicao = async () => {
    if (selecionadosParaAdicionar.length === 0) return;
    await fila.aguardar();
    try {
      await adicionarItens.mutateAsync(selecionadosParaAdicionar);
      toast({ title: "Períodos adicionados" });
      setPickerAberto(false);
      setSelecionadosParaAdicionar([]);
    } catch (erro) {
      reportarErro(erro);
    }
  };

  const confirmNewPeriod = async (body: NovoPeriodoBody) => {
    await fila.aguardar();
    try {
      await addNewPeriod.mutateAsync(body);
      toast({ title: "Período criado", description: "O projeto ficou em rascunho em Projetos." });
      setNewPeriodOpen(false);
    } catch (erro) {
      reportarErro(erro);
    }
  };

  const openNewPeriodFromPicker = () => {
    setPickerAberto(false);
    setNewPeriodOpen(true);
  };

  const confirmarCancelamento = async () => {
    if (!motivoCancelamento.trim()) return;
    try {
      await cancelar.mutateAsync(motivoCancelamento.trim());
      toast({ title: "Proposta cancelada" });
      setCancelarAberto(false);
      setMotivoCancelamento("");
    } catch (erro) {
      reportarErro(erro);
    }
  };

  const confirmarPasso = async (pedido: PedidoDePasso, body: CicloBody) => {
    await fila.aguardar();
    try {
      if (pedido.alvo === "proposta") {
        await mudarCicloDaProposta.mutateAsync({ acao: pedido.acao, body });
      } else if (pedido.inviteId) {
        await mudarCicloDoInvite.mutateAsync({ inviteId: pedido.inviteId, acao: pedido.acao, body });
      }
      toast({ title: MENSAGEM_DO_PASSO[pedido.acao] });
      setPedidoDePasso(null);
    } catch (erro) {
      reportarErro(erro);
    }
  };

  if (!id) return <ErrorState title="Proposta não encontrada" />;

  if (propostaQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-12 w-full" />
        <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          <Skeleton className="h-[480px] w-full" />
          <Skeleton className="h-[360px] w-full" />
        </div>
      </div>
    );
  }

  if (propostaQuery.isError || !proposta) {
    return <ErrorState onRetry={() => propostaQuery.refetch()} />;
  }

  const statusInfo = getPropostaStatusInfo(proposta.status);
  const editavel = propostaEhEditavel(proposta.status, proposta.os_enviada_ao_omie);
  const podeReabrir = !proposta.os_enviada_ao_omie && passosDaProposta(proposta.status).includes("reaberto");
  const pedirReabertura = () =>
    setPedidoDePasso({ acao: "reaberto", alvo: "proposta", nome: proposta.codigo ?? proposta.agencia.nome });
  const dadosAlteradosNoServidor = new Date(proposta.dados_alterados_em ?? proposta.updated_at);
  const dadosAlteradosEm =
    fila.ultimaGravacao && fila.ultimaGravacao > dadosAlteradosNoServidor ? fila.ultimaGravacao : dadosAlteradosNoServidor;
  const camposComPendencia = new Set(
    proposta.pendencias
      .map((pendencia) => pendencia.campo ?? (pendencia.codigo === "sem_competencia" ? "competencia" : null))
      .filter(
        (campo): campo is string =>
          !!campo && (CAMPOS_DA_AGENCIA.has(campo) || campo === "competencia" || campo.startsWith("cnpj-")),
      ),
  );
  const indiceEtapa = etapas.findIndex((item) => item.id === etapa);
  const proximaEtapa = etapas[indiceEtapa + 1];

  const acaoPrincipal =
    etapa === "documento" ? (
      <Button
        className="w-full gap-1.5"
        disabled={!editavel || gerarDocumento.isPending || proposta.pendencias_bloqueantes > 0}
        onClick={gerar}
      >
        {gerarDocumento.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        {proposta.documentos.length === 0 ? "Gerar proposta e invites" : "Gerar nova versão"}
      </Button>
    ) : proximaEtapa ? (
      <Button className="w-full gap-1.5" onClick={() => irParaEtapa(proximaEtapa.id)}>
        {etapa === "dados" ? "Revisar documento" : proximaEtapa.rotulo}
        <ArrowRight className="h-4 w-4" />
      </Button>
    ) : (
      <Button variant="outline" className="w-full gap-1.5" onClick={() => irParaEtapa("documento")}>
        <ArrowLeft className="h-4 w-4" />
        Voltar ao documento
      </Button>
    );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon" aria-label="Voltar" onClick={() => navigateBack("/financeiro")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">
              {proposta.codigo ?? "Proposta em rascunho"}
            </h1>
            <Badge variant="outline" className={statusInfo.badgeClass}>
              {statusInfo.label}
            </Badge>
            {proposta.origem && <Badge variant="outline">{ORIGEM_LABEL[proposta.origem] ?? proposta.origem}</Badge>}
            {!proposta.codigo && proposta.codigo_previsto && (
              <button
                type="button"
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => {
                  irParaEtapa("dados");
                  requestAnimationFrame(() => document.getElementById("campo-codigo")?.focus());
                }}
              >
                Código ao gerar o PDF: {proposta.codigo_previsto}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <Link to={`/agencias/${proposta.agencia.id}`} className="inline-flex items-center gap-1.5 hover:underline">
              <Building2 className="h-3.5 w-3.5" />
              {proposta.agencia.nome}
            </Link>
            {proposta.vaga && (
              <Link to={`/vagas/${proposta.vaga.id}`} className="text-primary hover:underline">
                {proposta.vaga.descricao_curta ?? "Ver vaga"}
              </Link>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {propostaPodeSerCancelada(proposta.status) && (
            <Button
              variant="outline"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={() => setCancelarAberto(true)}
            >
              <Ban className="h-4 w-4" />
              Cancelar proposta
            </Button>
          )}
          <AcoesDoCicloDaProposta
            status={proposta.status}
            onPedir={(acao) =>
              setPedidoDePasso({
                acao,
                alvo: "proposta",
                nome: proposta.codigo ?? proposta.agencia.nome,
                sugestaoPara: proposta.agencia.contato_email ?? undefined,
              })
            }
          />
        </div>
      </div>

      {proposta.status === "cancelada" && proposta.motivo_cancelamento && (
        <p className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          Cancelada: {proposta.motivo_cancelamento}
        </p>
      )}
      {proposta.status === "enviada" && proposta.enviada_em && (
        <p className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          Enviada em {formatDateOnly(proposta.enviada_em)}
          {proposta.enviada_para.length > 0 ? ` para ${proposta.enviada_para.join(", ")}` : ""}. Uma nova versão
          volta a proposta para gerada, até ser enviada de novo.
        </p>
      )}
      {proposta.status === "lancada_omie" && (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Todas as OS desta proposta estão no Omie. Daqui em diante, ajustes são feitos direto no Omie.
        </p>
      )}
      {proposta.status === "aceita" && proposta.aceita_em && (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Aceita em {formatDateOnly(proposta.aceita_em)}
          {proposta.aceita_por ? ` por ${proposta.aceita_por}` : ""}.
          {editavel && " Uma nova versão volta a proposta para gerada e pede um novo aceite."}
        </p>
      )}
      {proposta.status === "recusada" && proposta.motivo_recusa && (
        <p className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          Recusada{proposta.recusada_em ? ` em ${formatDateOnly(proposta.recusada_em)}` : ""}: {proposta.motivo_recusa}
        </p>
      )}

      <nav aria-label="Etapas da proposta" className="flex flex-wrap gap-1 border-b">
        {etapas.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => irParaEtapa(item.id)}
            aria-current={etapa === item.id ? "step" : undefined}
            className={cn(
              "-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
              etapa === item.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="text-[11px] tabular-nums opacity-70">{item.numero}</span>
            {item.rotulo}
          </button>
        ))}
      </nav>

      <div
        className={cn(
          "grid items-start gap-6",
          etapa === "documento" ? "xl:grid-cols-[minmax(0,1fr)_300px]" : "lg:grid-cols-[minmax(0,1fr)_300px]",
        )}
      >
        <div className="min-w-0">
          {etapa === "dados" && (
            <EtapaDados
              proposta={proposta}
              editavel={editavel}
              salvarProposta={salvarProposta}
              salvarAgencia={salvarAgencia}
              salvarCadastroDaAgencia={salvarCadastroDaAgencia}
              salvarCnpjDoFreelancer={salvarCnpjDoFreelancer}
              onAdicionarPeriodos={() => setPickerAberto(true)}
              onNewPeriod={() => setNewPeriodOpen(true)}
              onRemoverItem={(item, grupo) => setItemParaRemover({ item, grupo })}
              itemDestacado={itemDestacado}
              camposComPendencia={camposComPendencia}
            />
          )}
          {etapa === "documento" && (
            <EtapaDocumento
              proposta={proposta}
              editavel={editavel}
              motivoDoBloqueio={motivoDoBloqueioDaEdicao(proposta.status, proposta.os_enviada_ao_omie)}
              onReabrir={podeReabrir ? pedirReabertura : undefined}
              enfileirar={enfileirar}
              onTrocarModelo={(modelo) => salvarProposta({ modelo_documento: modelo })}
              onIrParaDados={() => irParaEtapa("dados")}
              onImprimir={imprimirDocumento}
              onGerar={gerar}
              gerando={gerarDocumento.isPending}
              onGerarInvite={gerarInvite}
              gerandoInviteDe={gerarInviteDoProfissional.isPending ? gerarInviteDoProfissional.variables : null}
              bloqueiosDeGeracao={proposta.pendencias_bloqueantes}
              dadosAlteradosEm={dadosAlteradosEm}
              onResolverPendencia={resolverPendencia}
              onBaixar={baixar}
              onBaixarInvite={baixarInvite}
              onPedirPassoDoInvite={(inviteId, nome, acao) =>
                setPedidoDePasso({ acao, alvo: "invite", inviteId, nome })
              }
            />
          )}
          {etapa === "os" && (
            <EtapaOrdensServico
              propostaId={propostaId}
              previa={ordensQuery.data}
              carregando={ordensQuery.isLoading}
              comErro={ordensQuery.isError}
              onRecarregar={() => ordensQuery.refetch()}
            />
          )}
        </div>
        <aside className={etapa === "documento" ? "xl:sticky xl:top-4" : "lg:sticky lg:top-4"}>
          <ResumoProposta
            proposta={proposta}
            salvando={fila.salvando}
            ultimaGravacao={fila.ultimaGravacao}
            onResolverPendencia={resolverPendencia}
            acaoPrincipal={acaoPrincipal}
          />
        </aside>
      </div>

      <Dialog open={pickerAberto} onOpenChange={setPickerAberto}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar períodos</DialogTitle>
            <DialogDescription>
              Períodos de {proposta.agencia.nome} que ainda não estão em outra proposta.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ProjetosElegiveisPicker
              agenciaId={proposta.agencia.id}
              selectedIds={selecionadosParaAdicionar}
              onToggle={(controleProjetoId) =>
                setSelecionadosParaAdicionar((atual) =>
                  atual.includes(controleProjetoId)
                    ? atual.filter((selecionado) => selecionado !== controleProjetoId)
                    : [...atual, controleProjetoId],
                )
              }
              excludePropostaId={proposta.id}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" className="sm:mr-auto" onClick={openNewPeriodFromPicker}>
              O período não está na lista? Novo período
            </Button>
            <Button type="button" variant="outline" onClick={() => setPickerAberto(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={selecionadosParaAdicionar.length === 0 || adicionarItens.isPending}
              onClick={confirmarAdicao}
              className="gap-1.5"
            >
              {adicionarItens.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Adicionar {selecionadosParaAdicionar.length || ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewPeriodDialog
        open={newPeriodOpen}
        agencyName={proposta.agencia.nome}
        saving={addNewPeriod.isPending}
        onOpenChange={setNewPeriodOpen}
        onConfirm={confirmNewPeriod}
      />

      <AlertDialog open={!!itemParaRemover} onOpenChange={(aberto) => !aberto && setItemParaRemover(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tirar este período da proposta?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemParaRemover?.grupo.freelancer.nome}. O projeto continua existindo e volta a ficar disponível
              para outra proposta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarRemocao}>Tirar da proposta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MarcarPassoDialog
        pedido={pedidoDePasso}
        salvando={mudarCicloDaProposta.isPending || mudarCicloDoInvite.isPending}
        avisoInviteAntesDoAceite={!propostaFoiAceita(proposta.status)}
        onFechar={() => setPedidoDePasso(null)}
        onConfirmar={confirmarPasso}
      />

      <Dialog open={cancelarAberto} onOpenChange={setCancelarAberto}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar proposta</DialogTitle>
            <DialogDescription>
              Os períodos voltam a ficar disponíveis para outra proposta e os invites são cancelados. Conte o motivo.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={motivoCancelamento}
            onChange={(event) => setMotivoCancelamento(event.target.value)}
            placeholder="Motivo do cancelamento"
            rows={3}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelarAberto(false)}>
              Voltar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!motivoCancelamento.trim() || cancelar.isPending}
              onClick={confirmarCancelamento}
              className="gap-1.5"
            >
              {cancelar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Cancelar proposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
